import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DeckModel, DeckService } from '@core/services/deck.service';
import {
  DeckFlashcardListModel,
  FlashcardModel,
  FlashcardService,
} from '@core/services/flashcard.service';
import { TextToSpeechService } from '@core/services/text-to-speech.service';
import {
  FlashcardAssetModel,
  FlashcardAssetOrigin,
  FlashcardAssetScope,
  FlashcardAssetService,
} from '@core/services/flashcard-asset.service';
import {
  AdaptiveFlashcardModality,
  ReviewGradeType,
  ReviewSourceType,
  StudySessionType,
  ShortTalkReviewMessage,
  StudySessionService,
} from '@core/services/study-session.service';
import { FlashcardType } from '@core/models/flashcard-type.enum';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import {
  DailyStudyGoalProgressModel,
  StudyGoalService,
} from '@core/services/study-goal.service';
import { UiTranslationService } from '@core/services/ui-translation.service';
import {
  Observable,
  catchError,
  concat,
  finalize,
  firstValueFrom,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  TrainCardSubType,
  TrainCardType,
  TrainAsset,
  TrainFlashcard,
  TrainMode,
} from './deck-train.models';
import { FallbackTrainCard } from './components/fallback-train-card/fallback-train-card';
import { TrainCardShell } from './components/train-card-shell/train-card-shell';
import {
  TRAINING_ANSWER_MAX_LENGTH,
  isTrainingConversationWithinLimits,
} from './training-input-limits';
import { ToastrService } from 'ngx-toastr';
import { TrainDailyGoalProgress } from './components/train-daily-goal-progress/train-daily-goal-progress';
import { isLikelySelfAssessmentAnswer } from '../flashcard-editors/shared/utils/flashcard-self-assessment.util';
import { isShortAnswerMatch } from '../flashcard-editors/shared/utils/flashcard-answer-validation.util';
import {
  FlashcardState,
  parseFlashcardState,
} from '../flashcard-editors/shared/types/flashcard-state.enum';
import {
  isDueFlashcard,
  sortDueFlashcards,
} from '@core/utils/flashcard-review-metrics.util';

type TrainMediaKind = 'image' | 'audio' | 'video' | 'unknown';
type TrainRouteSource = 'deck' | 'home' | 'mindmap' | 'mindmap-edit' | null;

type SessionReviewGradeCounts = Record<
  | ReviewGradeType.Again
  | ReviewGradeType.Hard
  | ReviewGradeType.Good
  | ReviewGradeType.Easy,
  number
>;

type FinishedReviewStat = {
  grade: ReviewGradeType;
  tone: 'again' | 'hard' | 'good' | 'easy';
  labelKey:
    | 'train.feedbackAgain'
    | 'train.feedbackHard'
    | 'train.feedbackGood'
    | 'train.feedbackEasy';
  count: number;
};

type FinishedTrainingI18n = {
  title: string;
  description: string;
  back: string;
  restart: string;
  summaryTitle: string;
  summarySubtitle: string;
  reviewedCards: string;
};

const TRAIN_VIDEO_FILE_EXTENSIONS = [
  'mp4',
  'webm',
  'ogg',
  'ogv',
  'mov',
  'm4v',
  'mkv',
  'avi',
];

const TRAIN_AUDIO_FILE_EXTENSIONS = [
  'mp3',
  'wav',
  'ogg',
  'oga',
  'm4a',
  'aac',
  'flac',
];

const QUESTION_AUTOPLAY_DELAY_MS = 500;
const AUDIO_CONVERSATION_AUTOPLAY_DELAY_MS = 2000;

@Component({
  selector: 'app-deck-train',
  imports: [
    FallbackTrainCard,
    TrainCardShell,
    TrainDailyGoalProgress,
    NgbDropdownModule,
  ],
  templateUrl: './deck-train.html',
  styleUrl: './deck-train.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeckTrain implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly deckService = inject(DeckService);
  private readonly flashcardService = inject(FlashcardService);
  private readonly assetService = inject(FlashcardAssetService);
  private readonly textToSpeechService = inject(TextToSpeechService);
  private readonly studySessionService = inject(StudySessionService);
  private readonly studyGoalService = inject(StudyGoalService);
  private readonly deckTranslations =
    inject(UiTranslationService).namespace('deck');
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly toastr = inject(ToastrService);

  readonly t = this.deckTranslations.t;
  protected readonly TrainCardType = TrainCardType;
  protected readonly TrainCardSubType = TrainCardSubType;
  protected readonly ReviewGradeType = ReviewGradeType;
  protected readonly finishedTrainingI18n = computed<FinishedTrainingI18n>(
    () => {
      const translations = this.t();
      const isPractice = this.isPracticeMode;

      return {
        title: isPractice
          ? translations['train.practiceFinishedTitle']
          : translations['train.finishedTitle'],
        description: isPractice
          ? translations['train.practiceFinishedDescription']
          : translations['train.finishedDescription'],
        back: translations['train.back'],
        restart: translations['train.restart'],
        summaryTitle: translations['train.finishedSummaryTitle'],
        summarySubtitle: isPractice
          ? translations['train.practiceFinishedSummarySubtitle']
          : translations['train.finishedSummarySubtitle'],
        reviewedCards: translations['train.finishedReviewedCards'],
      };
    },
  );
  protected readonly reviewGradeOptions = [
    ReviewGradeType.Again,
    ReviewGradeType.Hard,
    ReviewGradeType.Good,
    ReviewGradeType.Easy,
  ];
  protected readonly sessionReviewGradeCounts =
    signal<SessionReviewGradeCounts>(this.createEmptyReviewGradeCounts());
  protected readonly finishedReviewStats = computed<FinishedReviewStat[]>(
    () => {
      const counts = this.sessionReviewGradeCounts();

      return [
        {
          grade: ReviewGradeType.Easy,
          tone: 'easy',
          labelKey: 'train.feedbackEasy',
          count: counts[ReviewGradeType.Easy],
        },
        {
          grade: ReviewGradeType.Good,
          tone: 'good',
          labelKey: 'train.feedbackGood',
          count: counts[ReviewGradeType.Good],
        },
        {
          grade: ReviewGradeType.Hard,
          tone: 'hard',
          labelKey: 'train.feedbackHard',
          count: counts[ReviewGradeType.Hard],
        },
        {
          grade: ReviewGradeType.Again,
          tone: 'again',
          labelKey: 'train.feedbackAgain',
          count: counts[ReviewGradeType.Again],
        },
      ];
    },
  );
  protected readonly finishedReviewedCardsCount = computed(() =>
    this.finishedReviewStats().reduce((total, stat) => total + stat.count, 0),
  );

  deck: DeckModel | null = null;
  flashcards: TrainFlashcard[] = [];
  private hasTrainableCardsOutsideDueMode = false;
  private hasUnavailableAdaptiveTraining = false;
  trainMode: TrainMode = 'due';
  protected isPracticeMode = false;
  trainFilterTerms: string[] = [];
  trainFlashcardIds: number[] = [];
  private routeFrom: TrainRouteSource = null;
  isLoading = false;
  errorMessage = '';
  currentIndex = 0;
  isAnswerVisible = false;
  isFinished = false;
  typedOrVoiceUserAnswer = '';
  typedOrVoiceIsCorrect: boolean | null = null;
  multipleChoiceSelectedOptionId: number | null = null;
  multipleChoiceSelectedOptionIds: number[] = [];
  multipleChoiceIsCorrect: boolean | null = null;
  isReviewSaving = false;
  isReviewStarting = false;
  selectedReviewGrade = ReviewGradeType.None;
  protected readonly dailyGoalProgress =
    signal<DailyStudyGoalProgressModel | null>(null);
  protected readonly trainingSessionReviewedCardsCount = signal(0);
  protected readonly trainingSessionCardsTarget = signal(0);
  protected readonly showDailyGoalProgress = computed(() => {
    const progress = this.dailyGoalProgress();
    return Boolean(progress?.isActive && progress.dailyCardsTarget > 0);
  });
  protected readonly showTrainingSessionProgress = computed(
    () => this.trainingSessionCardsTarget() > 0,
  );
  protected readonly trainingSessionProgressAriaLabel = computed(() =>
    this.tr('train.sessionProgressAria', {
      reviewed: this.trainingSessionReviewedCardsCount(),
      target: this.trainingSessionCardsTarget(),
    }),
  );
  protected readonly dailyGoalReviewedCards = computed(() =>
    Math.round(this.dailyGoalProgress()?.reviewedCards ?? 0),
  );
  protected readonly dailyGoalCardsTarget = computed(() =>
    Math.round(this.dailyGoalProgress()?.dailyCardsTarget ?? 0),
  );
  protected readonly dailyGoalProgressAriaLabel = computed(() =>
    this.tr('train.dailyGoalProgressAria', {
      reviewed: this.dailyGoalReviewedCards(),
      target: this.dailyGoalCardsTarget(),
    }),
  );
  protected readonly dailyGoalProgressTooltip = computed(() => {
    const target = this.dailyGoalCardsTarget();
    const percent =
      target > 0
        ? Math.round(
            Math.min(Math.max(this.dailyGoalReviewedCards() / target, 0), 1) *
              100,
          )
        : 0;

    return this.tr('train.dailyGoalProgressTooltip', { percent });
  });
  protected readonly dailyGoalProgressTitle = computed(() => {
    const progress = this.dailyGoalProgress();
    return progress?.completed
      ? this.tr('train.dailyGoalCompletedTitle')
      : this.tr('train.dailyGoalProgressTitle');
  });
  protected readonly dailyGoalProgressSummary = computed(() => {
    const progress = this.dailyGoalProgress();

    if (!progress) {
      return '';
    }

    if (progress.completed) {
      return this.tr('train.dailyGoalCompletedSummary', {
        cards: Math.round(progress.reviewedCards),
        minutes: Math.round(progress.activeMinutes),
      });
    }

    return this.tr('train.dailyGoalProgressSummary', {
      cards: Math.round(progress.reviewedCards),
      cardTarget: Math.round(progress.dailyCardsTarget),
      minutes: Math.round(progress.activeMinutes),
      minuteTarget: Math.round(progress.dailyMinutesTarget),
    });
  });
  private studySessionId: number | null = null;
  private currentReviewLogId: number | null = null;
  private currentReviewFlashcardId: number | null = null;
  private reviewedFlashcardIds = new Set<number>();
  private startedShortTalkFlashcardIds = new Set<number>();
  private requestedShortTalkOpeningReplyFlashcardIds = new Set<number>();
  private shortTalkMessagesByFlashcardId = new Map<
    number,
    ShortTalkReviewMessage[]
  >();
  private currentAudioPlayback: HTMLAudioElement | null = null;
  private currentAudioUrl = '';
  private scheduledQuestionAudioTimeout: ReturnType<typeof setTimeout> | null =
    null;
  private scheduledConversationAudioTimeout: ReturnType<
    typeof setTimeout
  > | null = null;
  private autoplaySequenceId = 0;
  protected readonly shortTalkOpeningReplyRequestId = signal(0);

  ngOnInit(): void {
    const deckId = Number(this.route.snapshot.paramMap.get('deckId'));
    if (!Number.isFinite(deckId) || deckId <= 0) {
      this.errorMessage = this.tr('train.invalidDeck');
      this.cdr.markForCheck();
      return;
    }

    this.trainMode = this.resolveTrainMode(
      this.route.snapshot.queryParamMap.get('trainMode'),
    );
    this.isPracticeMode =
      this.route.snapshot.queryParamMap.get('studyMode') === 'practice';
    this.routeFrom = this.parseRouteSource(
      this.route.snapshot.queryParamMap.get('from'),
    );
    this.trainFilterTerms = this.resolveTrainFilterTerms();
    this.trainFlashcardIds = this.resolveTrainFlashcardIds();
    this.load(deckId);
  }

  ngOnDestroy(): void {
    this.clearCurrentShortTalkCache();
    this.stopAllPlayback();
  }

  get currentCard(): TrainFlashcard | null {
    return this.flashcards[this.currentIndex] ?? null;
  }

  get sessionProgressLabel(): string {
    if (this.flashcards.length === 0) {
      return '';
    }

    return this.tr('train.progress')
      .replace('{current}', String(this.currentIndex + 1))
      .replace('{total}', String(this.flashcards.length));
  }

  get displayDeckTitle(): string {
    const rawTitle = this.deck?.title || this.t()['train.breadcrumbTitle'];
    return rawTitle.replace(/^Deck\s*-\s*/i, '').trim();
  }

  get canGoPrevious(): boolean {
    return this.currentIndex > 0;
  }

  get isReviewBusy(): boolean {
    return this.isReviewSaving || this.isReviewStarting;
  }

  get canGoNext(): boolean {
    return this.currentIndex < this.flashcards.length - 1;
  }

  get emptyMessage(): string {
    if (this.hasUnavailableAdaptiveTraining) {
      return this.tr('train.adaptiveUnavailable');
    }

    return this.canTrainAllCardsFromEmptyState
      ? this.tr('train.emptyDue')
      : this.tr('train.empty');
  }

  get canTrainAllCardsFromEmptyState(): boolean {
    return (
      this.trainMode === 'due' &&
      (this.hasTrainableCardsOutsideDueMode ||
        this.toNumber(this.deck?.flashcardCount) > 0)
    );
  }

  tr(key: string, params?: Record<string, string | number>): string {
    return this.deckTranslations.tr(key, params);
  }

  get shortTalkSessionIdForCache(): number | null {
    return this.studySessionId;
  }

  get playingAudioUrl(): string {
    return this.currentAudioUrl;
  }

  toggleAnswer(): void {
    this.isAnswerVisible = !this.isAnswerVisible;
  }

  onCardInteraction(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      this.toggleAnswer();
      return;
    }

    const interactiveSelector = [
      'button',
      'a',
      'input',
      'select',
      'textarea',
      'label',
      'audio',
      'video',
      '[role="button"]',
    ].join(',');

    if (target.closest(interactiveSelector)) {
      return;
    }

    this.toggleAnswer();
  }

  previous(): void {
    if (!this.canGoPrevious || this.isReviewBusy) {
      return;
    }

    this.stopAllPlayback();
    this.currentIndex -= 1;
    this.isAnswerVisible = false;
    this.resetTypedOrVoiceAnswer();
    this.resetMultipleChoiceAnswer();
    this.resetReviewGrade();
    this.beginCurrentCardReview();
    this.scheduleCurrentCardQuestionAudio();
  }

  next(): void {
    if (this.isReviewBusy) {
      return;
    }

    const card = this.currentCard;
    if (
      card &&
      this.isAnswerVisible &&
      !this.reviewedFlashcardIds.has(card.reviewFlashcardId)
    ) {
      this.submitReview(card, true, () => this.advanceOrFinish());
      return;
    }

    this.advanceOrFinish();
  }

  private advanceOrFinish(): void {
    if (!this.canGoNext) {
      this.finish();
      return;
    }

    this.stopAllPlayback();
    this.currentIndex += 1;
    this.isAnswerVisible = false;
    this.resetTypedOrVoiceAnswer();
    this.resetMultipleChoiceAnswer();
    this.resetReviewGrade();
    this.beginCurrentCardReview();
    this.scheduleCurrentCardQuestionAudio();
  }

  finish(): void {
    if (this.isFinished) {
      return;
    }

    this.clearCurrentShortTalkCache();
    this.stopAllPlayback();
    this.isFinished = true;
    this.isAnswerVisible = true;
    this.resetTypedOrVoiceAnswer();
    this.resetMultipleChoiceAnswer();
    this.resetReviewGrade();
    this.finishStudySession();
  }

  continueCurrentCard(): void {
    if (this.isReviewBusy) {
      return;
    }

    const card = this.currentCard;
    if (!card) {
      return;
    }

    if (card?.cardType === TrainCardType.ShortTalk) {
      this.submitReview(card, true, () => this.advanceOrFinish());
      return;
    }

    if (card?.cardSubType === TrainCardSubType.TextTypedOrVoice) {
      this.continueTextTypedOrVoiceCard(card);
      return;
    }

    if (this.isChoiceCard(card)) {
      this.continueMultipleChoiceCard(card);
      return;
    }

    if (!this.isAnswerVisible) {
      this.revealCurrentBack(card);
      return;
    }

    this.next();
  }

  restart(): void {
    this.clearCurrentShortTalkCache();
    this.stopAllPlayback();
    this.startedShortTalkFlashcardIds = new Set<number>();
    this.requestedShortTalkOpeningReplyFlashcardIds = new Set<number>();
    this.shortTalkMessagesByFlashcardId.clear();
    const deckId = this.deck?.id ?? 0;
    if (deckId > 0) {
      this.load(deckId);
      return;
    }

    this.currentIndex = 0;
    this.isAnswerVisible = false;
    this.isFinished = false;
    this.currentReviewLogId = null;
    this.currentReviewFlashcardId = null;
    this.reviewedFlashcardIds = new Set<number>();
    this.trainingSessionReviewedCardsCount.set(0);
    this.trainingSessionCardsTarget.set(this.flashcards.length);
    this.resetSessionReviewGradeCounts();
    this.resetTypedOrVoiceAnswer();
    this.resetMultipleChoiceAnswer();
    this.resetReviewGrade();
    this.beginCurrentCardReview();
    this.scheduleCurrentCardQuestionAudio();
  }

  onReviewGradeChange(reviewGrade: ReviewGradeType): void {
    this.selectedReviewGrade = reviewGrade;
  }

  submitFeedbackReview(reviewGrade: ReviewGradeType): void {
    if (this.isReviewBusy) {
      return;
    }

    const card = this.currentCard;
    if (!card || card.cardType === TrainCardType.ShortTalk) {
      return;
    }

    this.selectedReviewGrade = reviewGrade;
    this.submitReview(card, this.wasCorrectFromReviewGrade(reviewGrade), () =>
      this.advanceOrFinish(),
    );
  }

  reviewGradeLabel(reviewGrade: ReviewGradeType): string {
    return ReviewGradeType[reviewGrade];
  }

  reviewGradeButtonLabel(reviewGrade: ReviewGradeType): string {
    return reviewGrade === ReviewGradeType.None
      ? 'Feedback'
      : this.reviewGradeLabel(reviewGrade);
  }

  back(): void {
    this.clearCurrentShortTalkCache();
    this.stopAllPlayback();
    const deckId = this.deck?.id;
    const mindMapId = this.resolveMindMapContextId();

    if (this.routeFrom === 'mindmap-edit') {
      const routeMindMapId = this.resolveRouteMindMapId();
      if (routeMindMapId) {
        this.router.navigate(['/mindmap-edit'], {
          queryParams: { id: routeMindMapId, fromRoute: 'mindmap' },
        });
        return;
      }

      this.router.navigate(['/mindmap']);
      return;
    }

    if (this.routeFrom === 'mindmap') {
      this.router.navigate(['/mindmap']);
      return;
    }

    if (this.routeFrom === 'home') {
      this.router.navigate(['/home']);
      return;
    }

    if (this.routeFrom === 'deck') {
      this.router.navigate(['/deck'], {
        queryParams: mindMapId ? { mindMapId } : undefined,
      });
      return;
    }

    if (deckId) {
      this.router.navigate(['/deck-edit', deckId], {
        queryParams: mindMapId ? { mindMapId } : undefined,
      });
      return;
    }

    this.router.navigate(['/deck']);
  }

  trainAllCards(): void {
    if (this.isLoading || this.trainMode !== 'due') {
      return;
    }

    const deckId =
      this.deck?.id ??
      this.toNumber(this.route.snapshot.paramMap.get('deckId'));
    if (deckId <= 0) {
      return;
    }

    this.trainMode = 'all';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { trainMode: 'all' },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.load(deckId);
  }

  canSpeakQuestion(card: TrainFlashcard): boolean {
    if (card.cardType === TrainCardType.Pronunciation) {
      return card.frontText.trim().length > 0;
    }

    const aiTtsVoiceId = this.resolveAiTtsVoiceId(card);
    return aiTtsVoiceId > 0;
  }

  async speakCurrentCard(): Promise<boolean> {
    const card = this.currentCard;
    if (!card) {
      return false;
    }

    const aiTtsVoiceId = this.resolveAiTtsVoiceId(card);
    if (card.cardType === TrainCardType.Pronunciation && aiTtsVoiceId <= 0) {
      try {
        await firstValueFrom(
          this.textToSpeechService.speak(
            { ...card, aiTtsVoiceId: 1 },
            card.frontText,
          ),
        );
        return true;
      } catch (error) {
        this.logAudioUnavailable(
          `Browser TTS failed for flashcard ${card.id}.`,
          error,
        );
        return false;
      }
    }

    if (aiTtsVoiceId <= 0) {
      return false;
    }

    if (aiTtsVoiceId > 1) {
      if (this.shouldUseOnDemandQuestionTts(card)) {
        try {
          await firstValueFrom(
            this.textToSpeechService.speak(card, card.frontText),
          );
          return true;
        } catch (error) {
          this.logAudioUnavailable(
            `On-demand question TTS failed for flashcard ${card.id}.`,
            error,
          );
          return false;
        }
      }

      const asset = this.resolveQuestionTtsAsset(card);
      if (!asset?.url) {
        this.logAudioUnavailable(
          `Missing front TTS audio asset for flashcard ${card.id}.`,
        );
        return false;
      }

      await this.playAssetUrl(asset.url);
      return true;
    }

    const text = this.buildShortTalkQuestionText(card);
    if (!text) {
      return false;
    }

    try {
      await firstValueFrom(this.textToSpeechService.speak(card, text));
      return true;
    } catch (error) {
      this.logAudioUnavailable(
        `Browser TTS failed for flashcard ${card.id}.`,
        error,
      );
      return false;
    }
  }

  private playQuestionAudioWithoutAwait(): void {
    void this.speakCurrentCard().catch((error: unknown) => {
      const cardId = this.currentCard?.id ?? 0;
      if (cardId > 0) {
        this.logAudioUnavailable(
          `Unable to play front question audio for flashcard ${cardId}.`,
          error,
        );
      }
    });
  }

  onShortTalkIntroStarted(): void {
    const card = this.currentCard;
    if (!card || card.cardType !== TrainCardType.ShortTalk) {
      return;
    }

    this.startedShortTalkFlashcardIds.add(card.id);
    this.scheduleCurrentCardQuestionAudio();
  }

  onPronunciationAssessmentComplete(): void {
    const card = this.currentCard;
    if (!card || card.cardType !== TrainCardType.Pronunciation) {
      return;
    }

    this.isAnswerVisible = true;
    this.resetReviewGrade();
    this.cdr.markForCheck();
  }

  isSupportedCard(card: TrainFlashcard): boolean {
    if (card.cardType === TrainCardType.ShortTalk) {
      return true;
    }

    return (
      this.isSupportedCardType(card.cardType) &&
      this.isSupportedCardSubType(card.cardSubType)
    );
  }

  resolveImageAsset(card: TrainFlashcard): TrainAsset | null {
    if (
      card.cardType !== TrainCardType.Text &&
      card.cardType !== TrainCardType.Image &&
      card.cardType !== TrainCardType.AudioRecord &&
      card.cardType !== TrainCardType.AudioConversation
    ) {
      return null;
    }

    return this.resolveAssetByKind(card, 'image');
  }

  resolveAudioAsset(card: TrainFlashcard): TrainAsset | null {
    if (
      card.cardType !== TrainCardType.AudioRecord &&
      card.cardType !== TrainCardType.AudioConversation
    ) {
      return null;
    }

    if (card.cardType === TrainCardType.AudioConversation) {
      return (
        card.assets.find(
          (asset) =>
            asset.scope === FlashcardAssetScope.Media &&
            this.resolveTrainAssetKind(asset) === 'audio',
        ) ?? null
      );
    }

    const manualAudioAsset = card.assets.find(
      (asset) =>
        this.isManualMediaAsset(asset) &&
        this.resolveTrainAssetKind(asset) === 'audio' &&
        asset.origin !== FlashcardAssetOrigin.GeneratedTts,
    );

    return manualAudioAsset ?? this.resolveAssetByKind(card, 'audio');
  }

  resolveVideoAsset(card: TrainFlashcard): TrainAsset | null {
    if (card.cardType !== TrainCardType.VideoOnly) {
      return null;
    }

    return this.resolveAssetByKind(card, 'video');
  }

  resolveQuestionAudioUrl(card: TrainFlashcard): string {
    if (this.resolveAiTtsVoiceId(card) <= 1) {
      return '';
    }

    return this.resolveQuestionTtsAsset(card)?.url ?? '';
  }

  playCurrentAudioAsset(): void {
    const card = this.currentCard;
    if (!card) {
      return;
    }

    const audioAsset = this.resolveAudioAsset(card);
    if (!audioAsset?.url) {
      return;
    }

    this.playAssetUrl(audioAsset.url);
  }

  playMultipleChoiceOption(optionId: number): void {
    const card = this.currentCard;
    if (!card || !Number.isFinite(optionId) || optionId <= 0) {
      return;
    }

    const generatedOptionAsset = this.resolveGeneratedTtsOptionAsset(
      card,
      optionId,
    );
    if (!generatedOptionAsset?.url) {
      return;
    }

    this.playAssetUrl(generatedOptionAsset.url);
  }

  stopPlayback(): void {
    this.stopAllPlayback();
  }

  resolveMultipleChoiceOptionPlayableIds(card: TrainFlashcard): number[] {
    if (!this.isChoiceCard(card)) {
      return [];
    }

    const optionIds = new Set(card.options.map((option) => option.id));
    return card.assets
      .filter(
        (asset) =>
          asset.scope === FlashcardAssetScope.BackOption &&
          asset.origin === FlashcardAssetOrigin.GeneratedTts &&
          this.resolveTrainAssetKind(asset) === 'audio' &&
          asset.targetId != null &&
          optionIds.has(asset.targetId),
      )
      .map((asset) => asset.targetId as number);
  }

  private scheduleCurrentCardQuestionAudio(): void {
    this.cancelScheduledQuestionAudio();
    this.cancelScheduledConversationAudio();

    const card = this.currentCard;
    if (!card) {
      return;
    }

    if (
      card.cardType === TrainCardType.ShortTalk &&
      !this.startedShortTalkFlashcardIds.has(card.id)
    ) {
      return;
    }

    const sequenceId = ++this.autoplaySequenceId;
    const scheduledCardId = card.id;
    const scheduledCardIndex = this.currentIndex;
    this.scheduledQuestionAudioTimeout = setTimeout(() => {
      this.scheduledQuestionAudioTimeout = null;

      if (
        !this.isCurrentAutoplayTarget(
          scheduledCardId,
          scheduledCardIndex,
          sequenceId,
        )
      ) {
        return;
      }

      void this.playCurrentCardIntroAudio(card, scheduledCardIndex, sequenceId);
    }, QUESTION_AUTOPLAY_DELAY_MS);
  }

  private async playCurrentCardIntroAudio(
    card: TrainFlashcard,
    scheduledCardIndex: number,
    sequenceId: number,
  ): Promise<void> {
    if (card.cardType === TrainCardType.AudioConversation) {
      if (!this.canSpeakQuestion(card)) {
        this.playCurrentAudioAsset();
        return;
      }

      const questionPlayed = await this.speakCurrentCard();
      if (
        !questionPlayed ||
        !this.isCurrentAutoplayTarget(card.id, scheduledCardIndex, sequenceId)
      ) {
        return;
      }

      this.scheduleCurrentAudioConversationMediaAudio(
        card,
        scheduledCardIndex,
        sequenceId,
      );
      return;
    }

    if (card.cardType === TrainCardType.ShortTalk) {
      await this.playShortTalkIntroAudio(card);
      if (
        this.isCurrentAutoplayTarget(card.id, scheduledCardIndex, sequenceId)
      ) {
        this.requestShortTalkOpeningReply(card);
      }
      return;
    }

    this.playQuestionAudioWithoutAwait();
  }

  private async playShortTalkIntroAudio(card: TrainFlashcard): Promise<void> {
    try {
      await this.speakCurrentCard();
    } catch (error) {
      this.logAudioUnavailable(
        `Unable to play ShortTalk intro audio for flashcard ${card.id}.`,
        error,
      );
    }
  }

  private requestShortTalkOpeningReply(card: TrainFlashcard): void {
    if (this.requestedShortTalkOpeningReplyFlashcardIds.has(card.id)) {
      return;
    }

    this.requestedShortTalkOpeningReplyFlashcardIds.add(card.id);
    this.shortTalkOpeningReplyRequestId.update((requestId) => requestId + 1);
    this.cdr.markForCheck();
  }

  private scheduleCurrentAudioConversationMediaAudio(
    card: TrainFlashcard,
    scheduledCardIndex: number,
    sequenceId: number,
  ): void {
    this.cancelScheduledConversationAudio();

    const scheduledCardId = card.id;
    this.scheduledConversationAudioTimeout = setTimeout(() => {
      this.scheduledConversationAudioTimeout = null;

      if (
        !this.isCurrentAutoplayTarget(
          scheduledCardId,
          scheduledCardIndex,
          sequenceId,
        )
      ) {
        return;
      }

      this.playCurrentAudioAsset();
    }, AUDIO_CONVERSATION_AUTOPLAY_DELAY_MS);
  }

  private isCurrentAutoplayTarget(
    scheduledCardId: number,
    scheduledCardIndex: number,
    sequenceId: number,
  ): boolean {
    return (
      this.autoplaySequenceId === sequenceId &&
      !this.isLoading &&
      !this.isFinished &&
      !this.errorMessage &&
      this.currentIndex === scheduledCardIndex &&
      this.currentCard?.id === scheduledCardId
    );
  }

  private cancelScheduledQuestionAudio(): void {
    if (!this.scheduledQuestionAudioTimeout) {
      return;
    }

    clearTimeout(this.scheduledQuestionAudioTimeout);
    this.scheduledQuestionAudioTimeout = null;
  }

  private cancelScheduledConversationAudio(): void {
    if (!this.scheduledConversationAudioTimeout) {
      return;
    }

    clearTimeout(this.scheduledConversationAudioTimeout);
    this.scheduledConversationAudioTimeout = null;
  }

  private stopCurrentAudioPlayback(): void {
    if (!this.currentAudioPlayback) {
      this.currentAudioUrl = '';
      this.cdr.markForCheck();
      return;
    }

    this.currentAudioPlayback.pause();
    this.currentAudioPlayback.currentTime = 0;
    this.currentAudioPlayback = null;
    this.currentAudioUrl = '';
    this.cdr.markForCheck();
  }

  private stopAllPlayback(): void {
    this.autoplaySequenceId += 1;
    this.cancelScheduledQuestionAudio();
    this.cancelScheduledConversationAudio();
    this.stopCurrentAudioPlayback();
    this.textToSpeechService.stop();
  }

  private clearCurrentShortTalkCache(): void {
    if (!this.studySessionId || this.studySessionId <= 0) {
      return;
    }

    this.textToSpeechService.clearCacheScopesByPrefix(
      `shorttalk:${this.studySessionId}:`,
    );
  }

  onTypedOrVoiceUserAnswerChange(answer: string): void {
    this.typedOrVoiceUserAnswer = answer;
    if (!this.isAnswerVisible) {
      this.typedOrVoiceIsCorrect = null;
    }
  }

  onShortTalkMessagesChange(messages: ShortTalkReviewMessage[]): void {
    const card = this.currentCard;
    if (!card || card.cardType !== TrainCardType.ShortTalk) {
      return;
    }

    this.shortTalkMessagesByFlashcardId.set(
      card.id,
      messages.map((message) => ({
        role: message.role,
        text: message.text,
      })),
    );
  }

  isTypedOrVoiceSelfAssessment(card: TrainFlashcard): boolean {
    return (
      card.cardSubType === TrainCardSubType.TextTypedOrVoice &&
      isLikelySelfAssessmentAnswer(card.backText)
    );
  }

  onMultipleChoiceSelectedOptionChange(optionId: number): void {
    this.multipleChoiceSelectedOptionId = optionId;
    this.multipleChoiceSelectedOptionIds = optionId == null ? [] : [optionId];
    if (!this.isAnswerVisible) {
      this.multipleChoiceIsCorrect = null;
    }
  }

  onMultipleChoiceSelectedOptionIdsChange(optionIds: number[]): void {
    this.multipleChoiceSelectedOptionIds = optionIds;
    this.multipleChoiceSelectedOptionId = optionIds[0] ?? null;
    if (!this.isAnswerVisible) {
      this.multipleChoiceIsCorrect = null;
    }
  }

  private load(deckId: number): void {
    this.clearCurrentShortTalkCache();
    this.studySessionId = null;
    this.startedShortTalkFlashcardIds = new Set<number>();
    this.requestedShortTalkOpeningReplyFlashcardIds = new Set<number>();
    this.shortTalkMessagesByFlashcardId.clear();
    this.stopAllPlayback();
    this.isLoading = true;
    this.errorMessage = '';
    this.hasTrainableCardsOutsideDueMode = false;
    this.hasUnavailableAdaptiveTraining = false;
    this.trainingSessionReviewedCardsCount.set(0);
    this.trainingSessionCardsTarget.set(0);
    this.dailyGoalProgress.set(null);
    if (!this.isPracticeMode) this.reloadDailyGoalProgress();
    this.deckService
      .get(deckId)
      .pipe(
        switchMap((deck) => {
          if (Boolean(deck.isPlanLocked)) {
            throw new Error(this.tr('train.deckPlanLocked'));
          }

          this.deck = deck;
          return this.studySessionService
            .create({
              deckId,
              sessionType: this.isPracticeMode
                ? StudySessionType.QuickPractice
                : StudySessionType.ScheduledReview,
            })
            .pipe(
              tap((session) => {
                this.studySessionId = this.toNumber(session.id);
              }),
              switchMap(() =>
                this.flashcardService.listByDeck(deckId, false, {
                  trainMode: this.trainMode,
                  filterTerms: this.trainFilterTerms,
                }),
              ),
            );
        }),
        switchMap((list) => this.resolveTrainingCards(deckId, list)),
        switchMap((list) => this.loadFlashcardDetails(list)),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (cards) => {
          this.flashcards = cards;
          this.trainingSessionCardsTarget.set(cards.length);
          this.currentIndex = 0;
          this.isAnswerVisible = false;
          this.isFinished = false;
          this.currentReviewLogId = null;
          this.currentReviewFlashcardId = null;
          this.reviewedFlashcardIds = new Set<number>();
          this.trainingSessionReviewedCardsCount.set(0);
          this.resetSessionReviewGradeCounts();
          this.resetTypedOrVoiceAnswer();
          this.resetMultipleChoiceAnswer();
          this.resetReviewGrade();
          this.beginCurrentCardReview();
          this.scheduleCurrentCardQuestionAudio();
          this.cdr.markForCheck();
        },
        error: (error: Error) => {
          this.flashcards = [];
          this.trainingSessionCardsTarget.set(0);
          this.trainingSessionReviewedCardsCount.set(0);
          this.cancelScheduledQuestionAudio();
          this.errorMessage = error?.message ?? this.tr('train.errorLoading');
          this.cdr.markForCheck();
        },
      });
  }

  private loadFlashcardDetails(list: DeckFlashcardListModel[]) {
    const cards = list
      .map((card) => ({
        source: card,
        id: this.toNumber(card.id),
      }))
      .filter((card) => card.id > 0);

    if (cards.length === 0) {
      return of([] as TrainFlashcard[]);
    }

    const adaptiveParentIds = new Set(
      cards
        .filter(
          (card) =>
            this.toNumber(card.source.cardType) === FlashcardType.Adaptive,
        )
        .map((card) => card.id),
    );

    return forkJoin(
      cards.map((card) => this.loadTrainingFlashcard(card.id, card.source)),
    ).pipe(
      map((resolved) => {
        const resolvedCards = resolved.filter(
          (card): card is TrainFlashcard => card !== null,
        );
        this.hasUnavailableAdaptiveTraining =
          adaptiveParentIds.size > 0 &&
          !resolvedCards.some((card) =>
            adaptiveParentIds.has(card.reviewFlashcardId),
          );

        return resolvedCards;
      }),
    );
  }

  private loadTrainingFlashcard(
    id: number,
    source: DeckFlashcardListModel,
  ): Observable<TrainFlashcard | null> {
    if (this.toNumber(source.cardType) !== FlashcardType.Adaptive) {
      return this.loadFlashcard(id, source);
    }

    return this.flashcardService.resolveAdaptiveTraining(id).pipe(
      switchMap((adaptive) => {
        const childId = this.toNumber(adaptive.flashcard?.id);
        const parentId = this.toNumber(adaptive.parentFlashcardId);
        if (childId <= 0 || parentId !== id || adaptive.modality <= 0) {
          return of(null);
        }

        return this.loadFlashcard(
          childId,
          source,
          parentId,
          adaptive.modality,
          adaptive.flashcard,
        );
      }),
      catchError(() => of(null)),
    );
  }

  private loadFlashcard(
    id: number,
    source: DeckFlashcardListModel,
    reviewFlashcardId = id,
    adaptiveModality?: AdaptiveFlashcardModality,
    resolvedFlashcard?: FlashcardModel,
  ): Observable<TrainFlashcard> {
    return forkJoin({
      flashcard: resolvedFlashcard
        ? of(resolvedFlashcard)
        : this.flashcardService.get(id),
      assets: this.assetService
        .listByFlashcard(id)
        .pipe(catchError(() => of([]))),
    }).pipe(
      switchMap(({ flashcard, assets }) =>
        this.resolveTrainingAssets(assets).pipe(
          map((resolvedAssets) =>
            this.normalizeFlashcard(
              id,
              flashcard,
              resolvedAssets,
              source,
              reviewFlashcardId,
              adaptiveModality,
            ),
          ),
        ),
      ),
    );
  }

  private normalizeFlashcard(
    fallbackId: number,
    flashcard: FlashcardModel,
    assets: TrainAsset[],
    source: DeckFlashcardListModel,
    reviewFlashcardId: number,
    adaptiveModality?: AdaptiveFlashcardModality,
  ): TrainFlashcard {
    return {
      id: this.toNumber(flashcard.id, fallbackId),
      reviewFlashcardId,
      adaptiveModality,
      frontText: this.toString(flashcard.frontText),
      backText: this.toString(flashcard.backText),
      explanation: this.toString(flashcard.explanation),
      shortTalkScenario: this.toString(flashcard.shortTalkScenario),
      shortTalkLearnerGoal: this.toString(flashcard.shortTalkLearnerGoal),
      shortTalkSuccessCriteria: this.toString(
        flashcard.shortTalkSuccessCriteria,
      ),
      shortTalkMaxTurns: this.toNumber(flashcard.shortTalkMaxTurns),
      cardType: this.toNumber(flashcard.cardType),
      cardSubType: this.toNumber(flashcard.cardSubType),
      languageCodeProvider: this.toString(flashcard.languageCodeProvider),
      aiTtsVoiceId: this.toNullableNumber(flashcard.aiTtsVoiceId),
      enableAiEvaluation: Boolean(flashcard.enableAiEvaluation),
      dueAt: this.toString(source.dueAt ?? source.vencimento),
      progress: this.toNumber(source.progress),
      options: this.normalizeFlashcardOptionsFromModel(flashcard),
      assets,
    };
  }

  private normalizeFlashcardOptionsFromModel(flashcard: FlashcardModel) {
    return (flashcard.options ?? [])
      .map((option) => ({
        id: this.toNumber(option.id),
        text: this.toString(option.optionText),
        isCorrect: Boolean(option.isCorrect),
      }))
      .filter((option) => option.id > 0 && option.text.length > 0);
  }

  private resolveTrainingAssets(
    assets: FlashcardAssetModel[],
  ): Observable<TrainAsset[]> {
    const normalizedAssets = assets
      .map((asset) => ({
        id: this.toNumber(asset.id),
        type: this.toNumber(asset.assetType),
        scope: this.toNumber(asset.assetScope),
        origin: this.toNumber(asset.assetOrigin),
        targetId: this.toNullableNumber(asset.targetId),
        jobId: this.toNullableNumber(asset.jobId),
        fileName: this.toString(asset.fileName),
        mimeType: this.toString(asset.mimeType),
        url: this.toString(asset.fileUrl),
        altText: this.toString(asset.altText),
      }))
      .filter((asset) => asset.url.length > 0);

    if (normalizedAssets.length === 0) {
      return of([]);
    }

    return forkJoin(
      normalizedAssets.map((asset) =>
        this.assetService
          .resolveBrowserUrl({ id: asset.id, fileUrl: asset.url })
          .pipe(
            map((resolved) => ({
              ...asset,
              url: resolved.url || asset.url,
            })),
            catchError(() => of(asset)),
          ),
      ),
    );
  }

  private toNumber(
    value: number | string | null | undefined,
    fallback = 0,
  ): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private toNullableNumber(
    value: number | string | null | undefined,
  ): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toString(value: string | null | undefined): string {
    return (value ?? '').toString().trim();
  }

  private resolveTrainMode(value: string | null): TrainMode {
    return value === 'all' ? 'all' : 'due';
  }

  private parseRouteSource(value: string | null): TrainRouteSource {
    if (
      value === 'deck' ||
      value === 'home' ||
      value === 'mindmap' ||
      value === 'mindmap-edit'
    ) {
      return value;
    }

    return null;
  }

  private resolveMindMapContextId(): number | null {
    const routeMindMapId = this.resolveRouteMindMapId();
    return routeMindMapId ?? this.parsePositiveNumber(this.deck?.mindMapId);
  }

  private resolveRouteMindMapId(): number | null {
    return this.parsePositiveNumber(
      this.route.snapshot.queryParamMap.get('mindMapId'),
    );
  }

  private parsePositiveNumber(
    value: number | string | null | undefined,
  ): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private resolveTrainFilterTerms(): string[] {
    const query = this.route.snapshot.queryParamMap;
    const multiValueTerms = query
      .getAll('filterTerm')
      .map((term) => term.trim())
      .filter(Boolean);

    if (multiValueTerms.length) {
      return this.uniqueTerms(multiValueTerms);
    }

    const csvTerms = (query.get('filterTerms') ?? '')
      .split(',')
      .map((term) => term.trim())
      .filter(Boolean);
    return this.uniqueTerms(csvTerms);
  }

  private uniqueTerms(terms: string[]): string[] {
    return terms.filter(
      (term, index, source) => source.indexOf(term) === index,
    );
  }

  private resolveTrainFlashcardIds(): number[] {
    const query = this.route.snapshot.queryParamMap;
    const values = [
      ...query.getAll('flashcardId'),
      ...(query.get('flashcardIds') ?? '').split(','),
    ];

    return values
      .map((value) => Number(value.trim()))
      .filter(
        (value, index, source) =>
          Number.isFinite(value) &&
          value > 0 &&
          source.indexOf(value) === index,
      );
  }

  private continueTextTypedOrVoiceCard(card: TrainFlashcard): void {
    if (this.typedOrVoiceUserAnswer.length > TRAINING_ANSWER_MAX_LENGTH) {
      this.toastr.warning(this.tr('train.answerTooLong'));
      return;
    }
    if (card.enableAiEvaluation) {
      if (!this.isAnswerVisible) {
        const answerText = this.typedOrVoiceUserAnswer.trim();
        this.typedOrVoiceIsCorrect = null;

        if (!answerText) {
          this.selectedReviewGrade = ReviewGradeType.Again;
          this.submitReview(card, false, () => this.revealCurrentBack(card));
          return;
        }

        this.submitReview(card, false, () => this.revealCurrentBack(card));
        return;
      }
      this.next();
      return;
    }

    if (!this.isAnswerVisible) {
      if (this.isTypedOrVoiceSelfAssessment(card)) {
        this.typedOrVoiceIsCorrect = null;
        this.revealCurrentBack(card);
        return;
      }

      const isCorrect = isShortAnswerMatch(
        this.typedOrVoiceUserAnswer,
        card.backText,
      );
      this.typedOrVoiceIsCorrect = isCorrect;
      this.revealCurrentBack(card);
      return;
    }

    if (this.isTypedOrVoiceSelfAssessment(card)) {
      return;
    }

    if (this.typedOrVoiceIsCorrect === false) {
      this.submitIncorrectAutoReview(card);
      return;
    }

    this.next();
  }

  private continueMultipleChoiceCard(card: TrainFlashcard): void {
    if (!this.isAnswerVisible) {
      this.multipleChoiceIsCorrect =
        card.cardSubType === TrainCardSubType.TextMultipleChoice
          ? this.isExactMultipleChoiceSelectionCorrect(card)
          : this.isSingleChoiceSelectionCorrect(card);
      this.revealCurrentBack(card);
      return;
    }

    if (this.multipleChoiceIsCorrect === false) {
      this.submitIncorrectAutoReview(card);
      return;
    }

    this.next();
  }

  private submitIncorrectAutoReview(card: TrainFlashcard): void {
    this.selectedReviewGrade = ReviewGradeType.Again;
    this.submitReview(card, false, () => this.advanceOrFinish());
  }

  private wasCorrectFromReviewGrade(reviewGrade: ReviewGradeType): boolean {
    return reviewGrade !== ReviewGradeType.Again;
  }

  private revealCurrentBack(card: TrainFlashcard): void {
    this.autoplaySequenceId += 1;
    this.cancelScheduledQuestionAudio();
    this.cancelScheduledConversationAudio();
    this.isAnswerVisible = true;
    this.speakCurrentBack(card);
  }

  private speakCurrentBack(card: TrainFlashcard): void {
    const aiTtsVoiceId = this.resolveAiTtsVoiceId(card);
    if (aiTtsVoiceId <= 0) {
      return;
    }

    if (this.isChoiceCard(card)) {
      this.speakMultipleChoiceBack(card, aiTtsVoiceId);
      return;
    }

    if (aiTtsVoiceId > 1) {
      const urls: string[] = [];
      if (card.backText.trim()) {
        const backAsset = this.resolveBackTtsAsset(card);
        if (backAsset?.url) {
          urls.push(backAsset.url);
        } else {
          this.logAudioUnavailable(
            `Missing back TTS audio asset for flashcard ${card.id}.`,
          );
        }
      }

      if (card.explanation.trim()) {
        const explanationAsset = this.resolveExplanationTtsAsset(card);
        if (explanationAsset?.url) {
          urls.push(explanationAsset.url);
        }
      }

      if (urls.length > 0) {
        void this.playAssetUrlsSequentially(urls);
      }
      return;
    }

    const texts = [card.backText, card.explanation]
      .map((text) => text.trim())
      .filter((text) => text.length > 0);
    if (texts.length === 0) {
      return;
    }

    concat(
      ...texts.map((text) => this.textToSpeechService.speak(card, text)),
    ).subscribe({
      error: (error) => {
        this.logAudioUnavailable(
          `Browser TTS failed while speaking answer for flashcard ${card.id}.`,
          error,
        );
      },
    });
  }

  private speakMultipleChoiceBack(
    card: TrainFlashcard,
    aiTtsVoiceId: number,
  ): void {
    const correctOptions = card.options.filter((option) => option.isCorrect);
    if (correctOptions.length === 0) {
      return;
    }

    if (aiTtsVoiceId > 1) {
      const explanationAsset = this.resolveExplanationTtsAsset(card);
      const urls = correctOptions
        .map((option) => this.resolveGeneratedTtsOptionAsset(card, option.id))
        .map((asset) => asset?.url ?? '')
        .filter((url) => url.length > 0);
      if (explanationAsset?.url) {
        urls.push(explanationAsset.url);
      }

      if (urls.length > 0) {
        void this.playAssetUrlsSequentially(urls);
      }
      return;
    }

    const texts = [
      ...correctOptions.map((option) => option.text),
      card.explanation,
    ]
      .map((text) => text.trim())
      .filter((text) => text.length > 0);
    if (texts.length === 0) {
      return;
    }

    concat(
      ...texts.map((text) => this.textToSpeechService.speak(card, text)),
    ).subscribe({
      error: (error) => {
        this.logAudioUnavailable(
          `Browser TTS failed while speaking multiple-choice answer for flashcard ${card.id}.`,
          error,
        );
      },
    });
  }

  private resetTypedOrVoiceAnswer(): void {
    this.typedOrVoiceUserAnswer = '';
    this.typedOrVoiceIsCorrect = null;
  }

  private resetMultipleChoiceAnswer(): void {
    this.multipleChoiceSelectedOptionId = null;
    this.multipleChoiceSelectedOptionIds = [];
    this.multipleChoiceIsCorrect = null;
  }

  private resetReviewGrade(): void {
    this.selectedReviewGrade = ReviewGradeType.None;
  }

  private filterFlashcardsByMode(
    list: DeckFlashcardListModel[],
  ): DeckFlashcardListModel[] {
    const activeCards = list.filter((card) => this.isTrainableCard(card));

    if (this.trainMode === 'all') {
      return [...activeCards];
    }

    return this.filterDueFlashcards(activeCards);
  }

  private resolveTrainingCards(
    deckId: number,
    list: DeckFlashcardListModel[],
  ): Observable<DeckFlashcardListModel[]> {
    const filteredCards = this.filterFlashcardsByRequestedIds(
      this.filterFlashcardsByMode(list),
    );
    if (this.trainMode !== 'due' || filteredCards.length > 0) {
      return of(filteredCards);
    }

    return this.flashcardService
      .listByDeck(deckId, false, {
        trainMode: 'all',
        filterTerms: this.trainFilterTerms,
      })
      .pipe(
        map((allCards) => {
          this.hasTrainableCardsOutsideDueMode = allCards.some((card) =>
            this.isTrainableCard(card),
          );
          return filteredCards;
        }),
        catchError(() => of(filteredCards)),
      );
  }

  private filterDueFlashcards(
    list: DeckFlashcardListModel[],
  ): DeckFlashcardListModel[] {
    return sortDueFlashcards(list.filter((card) => isDueFlashcard(card)));
  }

  private filterFlashcardsByRequestedIds(
    list: DeckFlashcardListModel[],
  ): DeckFlashcardListModel[] {
    if (this.trainFlashcardIds.length === 0) {
      return list;
    }

    const requestedIds = new Set(this.trainFlashcardIds);
    return list.filter((card) => requestedIds.has(this.toNumber(card.id)));
  }

  private submitReview(
    card: TrainFlashcard,
    wasCorrect: boolean,
    onSuccess?: () => void,
  ): void {
    if (
      card.cardSubType === TrainCardSubType.TextTypedOrVoice &&
      this.typedOrVoiceUserAnswer.length > TRAINING_ANSWER_MAX_LENGTH
    ) {
      this.toastr.warning(this.tr('train.answerTooLong'));
      return;
    }
    if (
      card.cardType === TrainCardType.ShortTalk &&
      !isTrainingConversationWithinLimits(
        this.shortTalkMessagesByFlashcardId.get(card.id) ?? [],
        100,
      )
    ) {
      this.toastr.warning(this.tr('train.conversationTooLong'));
      return;
    }
    if (
      !this.studySessionId ||
      this.reviewedFlashcardIds.has(card.reviewFlashcardId)
    ) {
      onSuccess?.();
      return;
    }

    if (
      this.currentReviewLogId == null ||
      this.currentReviewFlashcardId !== card.reviewFlashcardId
    ) {
      this.startReviewForCard(card, (reviewLogId) => {
        if (!reviewLogId) {
          return;
        }

        this.currentReviewLogId = reviewLogId;
        this.currentReviewFlashcardId = card.reviewFlashcardId;
        this.submitReview(card, wasCorrect, onSuccess);
      });
      return;
    }

    this.isReviewSaving = true;
    this.errorMessage = '';
    const summaryReviewGrade = this.resolveReviewGradeForSummary(
      this.selectedReviewGrade,
    );
    this.cdr.markForCheck();

    this.studySessionService
      .review(this.studySessionId, {
        reviewLogId: this.currentReviewLogId,
        deckId: this.deck?.id ?? 0,
        flashcardId: card.reviewFlashcardId,
        ...(card.adaptiveModality
          ? { adaptiveModality: card.adaptiveModality }
          : {}),
        wasCorrect,
        answerText:
          card.enableAiEvaluation &&
          card.cardSubType === TrainCardSubType.TextTypedOrVoice
            ? this.typedOrVoiceUserAnswer.trim()
            : undefined,
        reviewSource: this.isPracticeMode
          ? ReviewSourceType.QuickPractice
          : ReviewSourceType.Train,
        reviewGrade: this.selectedReviewGrade,
        shortTalkMessages:
          card.cardType === TrainCardType.ShortTalk
            ? (this.shortTalkMessagesByFlashcardId.get(card.id) ?? [])
            : undefined,
      })
      .pipe(
        finalize(() => {
          this.isReviewSaving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.currentReviewLogId = null;
          this.currentReviewFlashcardId = null;
          this.reviewedFlashcardIds.add(card.reviewFlashcardId);
          this.trainingSessionReviewedCardsCount.set(
            this.reviewedFlashcardIds.size,
          );
          this.incrementSessionReviewGradeCount(summaryReviewGrade);
          if (!this.isPracticeMode) this.incrementDailyGoalReviewedCards();
          onSuccess?.();
        },
        error: () => {
          this.errorMessage = this.getReviewError(card);
          this.cdr.markForCheck();
        },
      });
  }

  private getReviewError(card: TrainFlashcard): string {
    return card.adaptiveModality
      ? this.tr('train.reviewAdaptiveError')
      : this.tr('train.reviewError');
  }

  private beginCurrentCardReview(): void {
    const card = this.currentCard;
    this.currentReviewLogId = null;
    this.currentReviewFlashcardId = card?.reviewFlashcardId ?? null;

    if (
      !card ||
      !this.studySessionId ||
      this.isFinished ||
      this.reviewedFlashcardIds.has(card.reviewFlashcardId)
    ) {
      return;
    }

    this.startReviewForCard(card);
  }

  private startReviewForCard(
    card: TrainFlashcard,
    onSuccess?: (reviewLogId: number | null) => void,
  ): void {
    if (!this.studySessionId) {
      this.errorMessage = this.getStartReviewError(card);
      this.cdr.markForCheck();
      onSuccess?.(null);
      return;
    }

    this.isReviewStarting = true;
    this.errorMessage = '';
    this.cdr.markForCheck();
    this.studySessionService
      .startReview(this.studySessionId, {
        deckId: this.deck?.id ?? 0,
        flashcardId: card.reviewFlashcardId,
        reviewSource: this.isPracticeMode
          ? ReviewSourceType.QuickPractice
          : ReviewSourceType.Train,
        ...(card.adaptiveModality
          ? {
              adaptiveModality: card.adaptiveModality,
              adaptivePresentationFlashcardId: card.id,
            }
          : {}),
      })
      .pipe(
        finalize(() => {
          this.isReviewStarting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (reviewLog) => {
          if (this.currentCard?.id !== card.id) {
            onSuccess?.(null);
            return;
          }

          const reviewLogId = this.toNumber(reviewLog.id);
          if (reviewLogId <= 0) {
            this.errorMessage = this.getStartReviewError(card);
            this.currentReviewLogId = null;
            onSuccess?.(null);
            return;
          }

          this.currentReviewLogId = reviewLogId > 0 ? reviewLogId : null;
          this.currentReviewFlashcardId = card.reviewFlashcardId;
          this.cdr.markForCheck();
          onSuccess?.(this.currentReviewLogId);
        },
        error: () => {
          this.errorMessage = this.getStartReviewError(card);
          this.cdr.markForCheck();
          onSuccess?.(null);
        },
      });
  }

  private getStartReviewError(card: TrainFlashcard): string {
    return card.adaptiveModality
      ? this.tr('train.startAdaptiveReviewError')
      : this.tr('train.startReviewError');
  }

  private finishStudySession(): void {
    if (!this.studySessionId) {
      return;
    }

    this.studySessionService
      .finish(this.studySessionId, {
        cardsReviewedCount: this.reviewedFlashcardIds.size,
      })
      .subscribe({
        error: (error: Error) => {
          this.errorMessage = error?.message ?? this.tr('train.finishError');
          this.cdr.markForCheck();
        },
        next: () => {
          if (!this.isPracticeMode) {
            this.reloadDailyGoalProgress();
          }
        },
      });
  }

  private reloadDailyGoalProgress(): void {
    this.studyGoalService
      .getDailyProgress()
      .pipe(catchError(() => of<DailyStudyGoalProgressModel | null>(null)))
      .subscribe((progress) => {
        this.dailyGoalProgress.set(progress);
        this.cdr.markForCheck();
      });
  }

  private incrementDailyGoalReviewedCards(): void {
    this.dailyGoalProgress.update((progress) => {
      if (!progress?.isActive || progress.dailyCardsTarget <= 0) {
        return progress;
      }

      const nextReviewedCards =
        progress.reviewedCards >= progress.dailyCardsTarget
          ? progress.reviewedCards
          : progress.reviewedCards + 1;

      return {
        ...progress,
        reviewedCards: Math.min(nextReviewedCards, progress.dailyCardsTarget),
        cardsProgressPercent:
          (Math.min(nextReviewedCards, progress.dailyCardsTarget) /
            progress.dailyCardsTarget) *
          100,
        completed:
          Math.min(nextReviewedCards, progress.dailyCardsTarget) >=
          progress.dailyCardsTarget,
      };
    });
  }

  private createEmptyReviewGradeCounts(): SessionReviewGradeCounts {
    return {
      [ReviewGradeType.Again]: 0,
      [ReviewGradeType.Hard]: 0,
      [ReviewGradeType.Good]: 0,
      [ReviewGradeType.Easy]: 0,
    };
  }

  private resetSessionReviewGradeCounts(): void {
    this.sessionReviewGradeCounts.set(this.createEmptyReviewGradeCounts());
  }

  private resolveReviewGradeForSummary(
    reviewGrade: ReviewGradeType,
  ): keyof SessionReviewGradeCounts {
    return reviewGrade === ReviewGradeType.None
      ? ReviewGradeType.Good
      : reviewGrade;
  }

  private incrementSessionReviewGradeCount(
    reviewGrade: keyof SessionReviewGradeCounts,
  ): void {
    this.sessionReviewGradeCounts.update((counts) => ({
      ...counts,
      [reviewGrade]: counts[reviewGrade] + 1,
    }));
  }

  private resolveAiTtsVoiceId(card: TrainFlashcard): number {
    const aiTtsVoiceId = Number(card.aiTtsVoiceId ?? 0);
    return Number.isFinite(aiTtsVoiceId) ? aiTtsVoiceId : 0;
  }

  private shouldUseOnDemandQuestionTts(card: TrainFlashcard): boolean {
    return (
      card.cardType === TrainCardType.Text &&
      card.cardSubType === TrainCardSubType.TextSimple &&
      this.hasFillInBlankMarker(card.frontText)
    );
  }

  private hasFillInBlankMarker(text: string): boolean {
    return /_{2,}/.test(text) || /\[blank\]/i.test(text);
  }

  private buildShortTalkQuestionText(card: TrainFlashcard): string {
    const learnerGoal = card.shortTalkLearnerGoal.trim();
    if (learnerGoal) {
      return learnerGoal;
    }

    return card.frontText.trim();
  }

  private resolveQuestionTtsAsset(card: TrainFlashcard): TrainAsset | null {
    const frontAudioAssets = card.assets.filter(
      (asset) =>
        asset.scope === FlashcardAssetScope.Front &&
        this.resolveTrainAssetKind(asset) === 'audio',
    );

    return (
      frontAudioAssets.find(
        (asset) => asset.origin === FlashcardAssetOrigin.GeneratedTts,
      ) ??
      frontAudioAssets[0] ??
      null
    );
  }

  private resolveBackTtsAsset(card: TrainFlashcard): TrainAsset | null {
    const backAudioAssets = card.assets.filter(
      (asset) =>
        asset.scope === FlashcardAssetScope.Back &&
        this.resolveTrainAssetKind(asset) === 'audio',
    );

    return (
      backAudioAssets.find(
        (asset) => asset.origin === FlashcardAssetOrigin.GeneratedTts,
      ) ??
      backAudioAssets[0] ??
      null
    );
  }

  private resolveExplanationTtsAsset(card: TrainFlashcard): TrainAsset | null {
    const explanationAudioAssets = card.assets.filter(
      (asset) =>
        asset.scope === FlashcardAssetScope.Explanation &&
        this.resolveTrainAssetKind(asset) === 'audio',
    );

    return (
      explanationAudioAssets.find(
        (asset) => asset.origin === FlashcardAssetOrigin.GeneratedTts,
      ) ??
      explanationAudioAssets[0] ??
      null
    );
  }

  private resolveGeneratedTtsOptionAsset(
    card: TrainFlashcard,
    optionId: number,
  ): TrainAsset | null {
    return (
      card.assets.find(
        (asset) =>
          asset.scope === FlashcardAssetScope.BackOption &&
          asset.origin === FlashcardAssetOrigin.GeneratedTts &&
          asset.targetId === optionId &&
          this.resolveTrainAssetKind(asset) === 'audio',
      ) ?? null
    );
  }

  private async playAssetUrlsSequentially(urls: string[]): Promise<void> {
    for (const url of urls) {
      await this.playAssetUrl(url);
    }
  }

  private playAssetUrl(url: string): Promise<void> {
    const AudioCtor = globalThis.Audio;
    if (!AudioCtor) {
      this.logAudioUnavailable(
        `Audio API unavailable when trying to play asset URL: ${url}.`,
      );
      return Promise.resolve();
    }

    if (this.currentAudioPlayback && this.currentAudioUrl === url) {
      const audio = this.currentAudioPlayback;
      return new Promise<void>((resolve) => {
        const finish = (): void => {
          audio.removeEventListener?.('ended', finish);
          audio.removeEventListener?.('error', fail);
          if (this.currentAudioPlayback === audio) {
            this.currentAudioPlayback = null;
            this.currentAudioUrl = '';
          }
          this.cdr.markForCheck();
          resolve();
        };
        const fail = (error?: unknown): void => {
          this.logAudioUnavailable(
            `Failed to replay currently loaded audio asset URL: ${url}.`,
            error,
          );
          finish();
        };

        audio.addEventListener('ended', finish);
        const onError = (event: Event): void => fail(event);
        audio.addEventListener('error', onError);
        audio.currentTime = 0;
        try {
          void audio.play()?.catch(fail);
        } catch {
          fail();
        }
      });
    }

    this.stopCurrentAudioPlayback();

    const audio = new AudioCtor(url);
    this.currentAudioPlayback = audio;
    this.currentAudioUrl = url;
    this.cdr.markForCheck();

    return new Promise<void>((resolve) => {
      const finish = (): void => {
        audio.removeEventListener?.('ended', finish);
        audio.removeEventListener?.('error', onError);
        if (this.currentAudioPlayback === audio) {
          this.currentAudioPlayback = null;
          this.currentAudioUrl = '';
        }
        this.cdr.markForCheck();
        resolve();
      };
      const fail = (error?: unknown): void => {
        this.logAudioUnavailable(
          `Failed to play audio asset URL: ${url}.`,
          error,
        );
        finish();
      };
      const onError = (event: Event): void => fail(event);

      audio.addEventListener('ended', finish);
      audio.addEventListener('error', onError);
      try {
        void audio.play()?.catch(fail);
      } catch {
        fail();
      }
    });
  }

  private logAudioUnavailable(message: string, error?: unknown): void {
    if (error !== undefined) {
      console.error(`[DeckTrain] ${message}`, error);
      return;
    }

    console.error(`[DeckTrain] ${message}`);
  }

  private resolveAssetByKind(
    card: TrainFlashcard,
    expectedKind: Exclude<TrainMediaKind, 'unknown'>,
  ): TrainAsset | null {
    return (
      card.assets.find(
        (asset) =>
          this.isManualMediaAsset(asset) &&
          this.resolveTrainAssetKind(asset) === expectedKind,
      ) ?? null
    );
  }

  private isManualMediaAsset(asset: TrainAsset): boolean {
    return (
      asset.scope === FlashcardAssetScope.Media &&
      asset.origin !== FlashcardAssetOrigin.GeneratedTts
    );
  }

  private resolveTrainAssetKind(asset: TrainAsset): TrainMediaKind {
    if (asset.type === 7) {
      return 'video';
    }

    if (asset.type === 6) {
      return 'audio';
    }

    if (asset.type === 5) {
      return 'image';
    }

    const mimeType = (asset.mimeType ?? '').trim().toLowerCase();
    if (mimeType.startsWith('video/')) {
      return 'video';
    }

    if (mimeType.startsWith('audio/')) {
      return 'audio';
    }

    if (mimeType.startsWith('image/')) {
      return 'image';
    }

    const extension = this.resolveTrainAssetFileExtension(asset);
    if (TRAIN_VIDEO_FILE_EXTENSIONS.includes(extension)) {
      return 'video';
    }

    if (TRAIN_AUDIO_FILE_EXTENSIONS.includes(extension)) {
      return 'audio';
    }

    return 'unknown';
  }

  private resolveTrainAssetFileExtension(asset: TrainAsset): string {
    const sources = [asset.fileName, asset.url];
    for (const source of sources) {
      const extension = this.extractFileExtension(source);
      if (extension) {
        return extension;
      }
    }

    return '';
  }

  private extractFileExtension(value: string | null | undefined): string {
    const normalizedValue = (value ?? '').trim();
    if (!normalizedValue) {
      return '';
    }

    const withoutHash = normalizedValue.split('#')[0];
    const withoutQuery = withoutHash.split('?')[0];
    const fileName = withoutQuery.split('/').pop() ?? '';
    const extensionIndex = fileName.lastIndexOf('.');
    if (extensionIndex < 0 || extensionIndex === fileName.length - 1) {
      return '';
    }

    return fileName.slice(extensionIndex + 1).toLowerCase();
  }

  private isSupportedCardType(cardType: number): boolean {
    return (
      cardType === TrainCardType.Text ||
      cardType === TrainCardType.Image ||
      cardType === TrainCardType.AudioRecord ||
      cardType === TrainCardType.VideoOnly ||
      cardType === TrainCardType.ShortTalk ||
      cardType === TrainCardType.AudioConversation ||
      cardType === TrainCardType.Pronunciation
    );
  }

  private isTrainableCard(card: DeckFlashcardListModel): boolean {
    if (Boolean(card.isPlanLocked)) {
      return false;
    }

    const state = parseFlashcardState(card.state, FlashcardState.New);
    return (
      state !== FlashcardState.InProcess && state !== FlashcardState.Suspended
    );
  }

  private isSupportedCardSubType(cardSubType: number): boolean {
    return (
      cardSubType === TrainCardSubType.TextSimple ||
      cardSubType === TrainCardSubType.TextTypedOrVoice ||
      cardSubType === TrainCardSubType.TextSingleChoice ||
      cardSubType === TrainCardSubType.TextMultipleChoice
    );
  }

  private isChoiceCard(card: TrainFlashcard): boolean {
    return (
      card.cardSubType === TrainCardSubType.TextSingleChoice ||
      card.cardSubType === TrainCardSubType.TextMultipleChoice
    );
  }

  private isSingleChoiceSelectionCorrect(card: TrainFlashcard): boolean {
    const selectedOption = card.options.find(
      (option) => option.id === this.multipleChoiceSelectedOptionId,
    );
    return Boolean(selectedOption?.isCorrect);
  }

  private isExactMultipleChoiceSelectionCorrect(card: TrainFlashcard): boolean {
    const selectedIds = new Set(this.multipleChoiceSelectedOptionIds);
    const correctIds = card.options
      .filter((option) => option.isCorrect)
      .map((option) => option.id);

    return (
      selectedIds.size === correctIds.length &&
      correctIds.every((optionId) => selectedIds.has(optionId))
    );
  }
}
